import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { getCustomers, createCustomer, updateCustomer, getCustomerHistory } from "../api";
import { useNotification } from "../NotificationContext";
import SkeletonLoader from "../components/SkeletonLoader";

const CustomersPage = () => {
    const navigate = useNavigate();
    const { addNotification } = useNotification();

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [formData, setFormData] = useState({
        member_id: "",
        name: "",
        phone: "",
        line_id: "",
        birthday: "",
        image_url: "",
        notes: "",
    });

    // History state
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [historyPage, setHistoryPage] = useState(1);
    const historyPerPage = 10;

    // Deposit History state
    const [showDepositHistoryModal, setShowDepositHistoryModal] = useState(false);
    const [depositHistory, setDepositHistory] = useState([]);
    const [loadingDepositHistory, setLoadingDepositHistory] = useState(false);

    const loadCustomers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getCustomers();
            setCustomers(res.data || []);
        } catch (err) {
            addNotification("โหลดข้อมูลลูกค้าไม่สำเร็จ", "danger");
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    useEffect(() => {
        loadCustomers();
    }, [loadCustomers]);

    const handleSearch = (e) => {
        setSearch(e.target.value);
    };

    const filteredCustomers = customers.filter((c) => {
        const term = search.toLowerCase();
        return (
            c.name.toLowerCase().includes(term) ||
            (c.phone && c.phone.includes(term)) ||
            (c.line_id && c.line_id.toLowerCase().includes(term)) ||
            (c.member_id && c.member_id.toLowerCase().includes(term))
        );
    });

    // Calculate pagination
    const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentCustomers = filteredCustomers.slice(startIndex, startIndex + itemsPerPage);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [search]);

    const handleOpenModal = (customer = null) => {
        if (customer) {
            setEditingCustomer(customer);
            setFormData({
                member_id: customer.member_id || "",
                name: customer.name,
                phone: customer.phone || "",
                line_id: customer.line_id || "",
                birthday: customer.birthday || "",
                image_url: customer.image_url || "",
                notes: customer.notes || "",
            });
        } else {
            setEditingCustomer(null);
            setFormData({
                member_id: "",
                name: "",
                phone: "",
                line_id: "",
                birthday: "",
                image_url: "",
                notes: "",
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingCustomer(null);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingCustomer) {
                await updateCustomer(editingCustomer.id, formData);
                addNotification("แก้ไขข้อมูลลูกค้าสำเร็จ", "success");
            } else {
                await createCustomer(formData);
                addNotification("เพิ่มลูกค้าสำเร็จ", "success");
            }
            handleCloseModal();
            loadCustomers();
        } catch (err) {
            addNotification(err.response?.data?.message || "บันทึกข้อมูลไม่สำเร็จ", "danger");
        }
    };

    const handleViewHistory = async (customer) => {
        setSelectedCustomer(customer);
        setShowHistoryModal(true);
        setLoadingHistory(true);
        setHistoryPage(1); // Reset to first page
        try {
            const res = await getCustomerHistory(customer.id);
            setHistory(res.data || []);
        } catch (err) {
            addNotification("โหลดประวัติไม่สำเร็จ", "danger");
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleViewDepositHistory = async (customer) => {
        setSelectedCustomer(customer);
        setShowDepositHistoryModal(true);
        setLoadingDepositHistory(true);
        try {
            // Call API: GET /customers/:id/deposits
            const res = await api.get(`/customers/${customer.id}/deposits?active_only=false`);
            // The API response structure for /customers/:id/deposits depends on backend implementation
            // Assuming it returns an array directly based on previous files, 
            // OR checks backend routes.py
            setDepositHistory(res.data || []);
        } catch (err) {
            addNotification("โหลดประวัติมัดจำไม่สำเร็จ", "danger");
            setDepositHistory([]);
        } finally {
            setLoadingDepositHistory(false);
        }
    };

    const handleViewDetail = (customer) => {
        setSelectedCustomer(customer);
        setShowDetailModal(true);
    };

    return (
        <div className="dashboard-page">
            <div className="dashboard-hero mb-3">
                <div>
                    <div className="dashboard-hero-title">
                        <i className="bi bi-people-fill me-2"></i>VIP Member (CRM)
                    </div>
                    <div className="dashboard-hero-subtitle">
                        จัดการฐานข้อมูลลูกค้า ประวัติการใช้บริการ และบันทึกช่วยจำ
                    </div>
                </div>
                <button className="btn btn-ks-primary" style={{ color: '#ffffff' }} onClick={() => handleOpenModal()}>
                    <i className="bi bi-person-plus me-1"></i>เพิ่มลูกค้าใหม่
                </button>
            </div>

            <div className="card card-ks-soft mb-4">
                <div className="card-body">
                    <div className="mb-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="ค้นหาลูกค้า (ชื่อ, เบอร์โทร, Line ID)..."
                            value={search}
                            onChange={handleSearch}
                        />
                    </div>

                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: "60px" }}></th>
                                    <th>Member ID</th>
                                    <th>ชื่อลูกค้า</th>
                                    <th>เบอร์โทร</th>
                                    <th>Line ID</th>
                                    <th>วันเกิด</th>
                                    <th>Note</th>
                                    <th className="text-end">จัดการ</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="8" className="py-3">
                                            <SkeletonLoader type="table" rows={8} columns={7} />
                                        </td>
                                    </tr>
                                ) : filteredCustomers.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="text-center py-4 text-muted">
                                            ไม่พบข้อมูลลูกค้า
                                        </td>
                                    </tr>
                                ) : (
                                    currentCustomers.map((c) => (
                                        <tr key={c.id}>
                                            <td>
                                                {c.image_url ? (
                                                    <img
                                                        src={c.image_url}
                                                        alt={c.name}
                                                        className="rounded-circle"
                                                        style={{ width: "40px", height: "40px", objectFit: "cover" }}
                                                    />
                                                ) : (
                                                    <div
                                                        className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                                                        style={{ width: "40px", height: "40px", fontSize: "1.2rem" }}
                                                    >
                                                        <i className="bi bi-person"></i>
                                                    </div>
                                                )}
                                            </td>
                                            <td><span className="badge bg-light text-dark border">{c.member_id || "-"}</span></td>
                                            <td className="fw-bold">{c.name}</td>
                                            <td>{c.phone || "-"}</td>
                                            <td>{c.line_id || "-"}</td>
                                            <td>{c.birthday || "-"}</td>
                                            <td>
                                                {c.notes ? (
                                                    <span className="badge text-dark text-wrap" style={{ maxWidth: "200px", backgroundColor: "#f3e5ab" }}>
                                                        {c.notes}
                                                    </span>
                                                ) : (
                                                    "-"
                                                )}
                                            </td>
                                            <td className="text-end">
                                                <button
                                                    className="btn btn-sm me-2 text-white"
                                                    style={{ backgroundColor: '#d4af37', border: '1px solid #d4af37' }}
                                                    onClick={() => handleViewDetail(c)}
                                                    title="ดูบัตรสมาชิก"
                                                >
                                                    <i className="bi bi-person-badge"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary me-2"
                                                    onClick={() => handleViewHistory(c)}
                                                    title="ดูประวัติ"
                                                >
                                                    <i className="bi bi-clock-history"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-success me-2"
                                                    onClick={() => handleViewDepositHistory(c)}
                                                    title="ประวัติมัดจำ"
                                                >
                                                    <i className="bi bi-wallet2"></i>
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => handleOpenModal(c)}
                                                    title="แก้ไข"
                                                >
                                                    <i className="bi bi-pencil"></i>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination Controls */}
                    {filteredCustomers.length > 0 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="text-muted small">
                                แสดง {startIndex + 1} ถึง {Math.min(startIndex + itemsPerPage, filteredCustomers.length)} จาก {filteredCustomers.length} รายการ
                            </div>
                            <nav>
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                                            <i className="bi bi-chevron-left"></i>
                                        </button>
                                    </li>
                                    {[...Array(totalPages)].map((_, i) => {
                                        const pageNum = i + 1;
                                        // Show first, last, current and adjacent pages
                                        if (totalPages > 7) {
                                            if (pageNum === 1 || pageNum === totalPages || Math.abs(currentPage - pageNum) <= 1) {
                                                return (
                                                    <li key={i} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                        <button className="page-link" onClick={() => setCurrentPage(pageNum)}>
                                                            {pageNum}
                                                        </button>
                                                    </li>
                                                );
                                            } else if (Math.abs(currentPage - pageNum) === 2) {
                                                return <li key={i} className="page-item disabled"><span className="page-link">...</span></li>;
                                            }
                                            return null;
                                        }
                                        return (
                                            <li key={i} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                                <button className="page-link" onClick={() => setCurrentPage(pageNum)}>
                                                    {pageNum}
                                                </button>
                                            </li>
                                        );
                                    })}
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                                            <i className="bi bi-chevron-right"></i>
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal: Add/Edit Customer */}
            {showModal && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingCustomer ? "แก้ไขข้อมูลลูกค้า" : "เพิ่มลูกค้าใหม่"}
                                </h5>
                                <button type="button" className="btn-close" onClick={handleCloseModal}></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row mb-3">
                                        <div className="col-md-4">
                                            <label className="form-label">Member ID</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="member_id"
                                                placeholder={editingCustomer ? "VIP-XXXX" : "Auto-generated"}
                                                value={formData.member_id || ""}
                                                onChange={handleChange}
                                                disabled={true}
                                            />
                                        </div>
                                        <div className="col-md-8">
                                            <label className="form-label">ชื่อลูกค้า <span className="text-danger">*</span></label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">เบอร์โทรศัพท์</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label className="form-label">Line ID</label>
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="line_id"
                                                value={formData.line_id}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">วันเกิด</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            name="birthday"
                                            value={formData.birthday}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">รูปโปรไฟล์ (URL)</label>
                                        <div className="d-flex gap-2">
                                            <input
                                                type="text"
                                                className="form-control"
                                                name="image_url"
                                                placeholder="https://example.com/image.jpg"
                                                value={formData.image_url}
                                                onChange={handleChange}
                                            />
                                            {formData.image_url && (
                                                <img
                                                    src={formData.image_url}
                                                    alt="Preview"
                                                    className="rounded border"
                                                    style={{ width: "38px", height: "38px", objectFit: "cover" }}
                                                    onError={(e) => (e.target.style.display = "none")}
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">บันทึกช่วยจำ (Note)</label>
                                        <textarea
                                            className="form-control"
                                            name="notes"
                                            rows="3"
                                            placeholder="เช่น แพ้น้ำยา..., สูตรสี..., ชอบนวดแรง..."
                                            value={formData.notes}
                                            onChange={handleChange}
                                        ></textarea>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                                        ยกเลิก
                                    </button>
                                    <button type="submit" className="btn text-white" style={{ backgroundColor: '#d4af37', border: 'none' }}>
                                        บันทึก
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: History */}
            {showHistoryModal && selectedCustomer && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    ประวัติการใช้บริการ: {selectedCustomer.name} <span className="text-muted small">(ล่าสุด 50 รายการ)</span>
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowHistoryModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {loadingHistory ? (
                                    <SkeletonLoader type="table" rows={6} columns={4} />
                                ) : history.length === 0 ? (
                                    <div className="text-center py-3 text-muted">ไม่พบประวัติการใช้บริการ</div>
                                ) : (
                                    <>
                                        <div className="table-responsive">
                                            <table className="table table-sm table-bordered">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>วันที่</th>
                                                        <th>เลขบิล</th>
                                                        <th>รายการ</th>
                                                        <th className="text-end">ยอดรวม</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(() => {
                                                        const totalHistoryPages = Math.ceil(history.length / historyPerPage);
                                                        const historyStartIndex = (historyPage - 1) * historyPerPage;
                                                        const currentHistory = history.slice(historyStartIndex, historyStartIndex + historyPerPage);

                                                        return currentHistory.map((h) => (
                                                            <tr key={h.order_id}>
                                                                <td>{new Date(h.date).toLocaleDateString("th-TH")}</td>
                                                                <td>{h.order_code}</td>
                                                                <td>
                                                                    <ul className="mb-0 ps-3">
                                                                        {h.items.map((item, idx) => (
                                                                            <li key={idx}>
                                                                                {item.service_name} (x{item.qty})
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </td>
                                                                <td className="text-end">{h.total_amount.toLocaleString()} ฿</td>
                                                            </tr>
                                                        ));
                                                    })()}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* History Pagination */}
                                        {history.length > historyPerPage && (
                                            <div className="d-flex justify-content-between align-items-center mt-3">
                                                <div className="text-muted small">
                                                    แสดง {((historyPage - 1) * historyPerPage) + 1} ถึง {Math.min(historyPage * historyPerPage, history.length)} จาก {history.length} รายการ
                                                </div>
                                                <nav>
                                                    <ul className="pagination pagination-sm mb-0">
                                                        <li className={`page-item ${historyPage === 1 ? 'disabled' : ''}`}>
                                                            <button className="page-link" onClick={() => setHistoryPage(prev => Math.max(prev - 1, 1))}>
                                                                <i className="bi bi-chevron-left"></i>
                                                            </button>
                                                        </li>
                                                        {[...Array(Math.ceil(history.length / historyPerPage))].map((_, i) => (
                                                            <li key={i} className={`page-item ${historyPage === i + 1 ? 'active' : ''}`}>
                                                                <button className="page-link" onClick={() => setHistoryPage(i + 1)}>
                                                                    {i + 1}
                                                                </button>
                                                            </li>
                                                        ))}
                                                        <li className={`page-item ${historyPage === Math.ceil(history.length / historyPerPage) ? 'disabled' : ''}`}>
                                                            <button className="page-link" onClick={() => setHistoryPage(prev => Math.min(prev + 1, Math.ceil(history.length / historyPerPage)))}>
                                                                <i className="bi bi-chevron-right"></i>
                                                            </button>
                                                        </li>
                                                    </ul>
                                                </nav>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowHistoryModal(false)}>
                                    ปิด
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Modal: VIP Card Detail */}
            {showDetailModal && selectedCustomer && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.6)" }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: "1rem", overflow: "hidden" }}>
                            {/* Header with Gold Gradient */}
                            <div className="modal-header border-0 text-white" style={{ background: "linear-gradient(135deg, #b8860b 0%, #f3e5ab 100%)" }}>
                                <h5 className="modal-title fw-bold text-dark">
                                    <i className="bi bi-star-fill me-2 text-white"></i>VIP Member Card
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowDetailModal(false)}></button>
                            </div>

                            <div className="modal-body p-0">
                                {/* Profile Section */}
                                <div className="text-center bg-white pb-4 pt-5 position-relative">
                                    <div className="position-absolute top-0 start-50 translate-middle-x" style={{ marginTop: "-1px" }}>
                                        <div style={{
                                            width: "100%",
                                            height: "20px",
                                            background: "linear-gradient(135deg, #b8860b 0%, #f3e5ab 100%)",
                                            borderBottomLeftRadius: "50%",
                                            borderBottomRightRadius: "50%"
                                        }}></div>
                                    </div>

                                    {selectedCustomer.image_url ? (
                                        <img
                                            src={selectedCustomer.image_url}
                                            alt={selectedCustomer.name}
                                            className="rounded-circle shadow-sm border border-4 border-white"
                                            style={{ width: "150px", height: "150px", objectFit: "cover" }}
                                        />
                                    ) : (
                                        <div
                                            className="rounded-circle shadow-sm border border-4 border-white bg-secondary text-white d-inline-flex align-items-center justify-content-center"
                                            style={{ width: "150px", height: "150px", fontSize: "4rem" }}
                                        >
                                            <i className="bi bi-person"></i>
                                        </div>
                                    )}

                                    <h3 className="mt-3 fw-bold text-dark mb-1">{selectedCustomer.name}</h3>
                                    {selectedCustomer.member_id && (
                                        <div className="mb-2">
                                            <span className="badge text-white px-3 py-1 rounded-pill shadow-sm" style={{ backgroundColor: '#d4af37' }}>
                                                ID: {selectedCustomer.member_id}
                                            </span>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-center gap-3 text-muted">
                                        <span><i className="bi bi-telephone-fill me-1"></i>{selectedCustomer.phone || "-"}</span>
                                        {selectedCustomer.line_id && (
                                            <span className="text-success"><i className="bi bi-line me-1"></i>{selectedCustomer.line_id}</span>
                                        )}
                                    </div>
                                </div>

                                {/* Info Cards */}
                                <div className="p-4 bg-light">
                                    <div className="row g-3">
                                        <div className="col-6">
                                            <div className="p-3 border rounded bg-white h-100 text-center shadow-sm">
                                                <small className="text-muted text-uppercase d-block mb-1" style={{ fontSize: "0.75rem" }}>Birthday</small>
                                                <span className="fw-bold fs-5">
                                                    {selectedCustomer.birthday ? new Date(selectedCustomer.birthday).toLocaleDateString('th-TH', { day: 'numeric', month: 'long' }) : "-"}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="p-3 border rounded bg-white h-100 text-center shadow-sm">
                                                <small className="text-muted text-uppercase d-block mb-1" style={{ fontSize: "0.75rem" }}>Status</small>
                                                <span className="fw-bold fs-5 text-warning">
                                                    <i className="bi bi-award-fill me-1"></i>VIP
                                                </span>
                                            </div>
                                        </div>
                                        <div className="col-12">
                                            <div className="p-3 border rounded bg-white shadow-sm">
                                                <small className="text-muted text-uppercase d-block mb-2" style={{ fontSize: "0.75rem" }}>
                                                    <i className="bi bi-journal-text me-1"></i>Notes / Preferences
                                                </small>
                                                <p className="mb-0 text-dark" style={{ whiteSpace: "pre-wrap" }}>
                                                    {selectedCustomer.notes || "ไม่มีบันทึก"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer border-0 bg-light justify-content-center pb-4">
                                <button className="btn btn-outline-secondary px-4 rounded-pill" onClick={() => setShowDetailModal(false)}>
                                    ปิด
                                </button>
                                <button className="btn px-4 rounded-pill text-white" style={{ backgroundColor: '#d4af37', border: 'none' }} onClick={() => { setShowDetailModal(false); handleOpenModal(selectedCustomer); }}>
                                    <i className="bi bi-pencil me-1"></i>แก้ไขข้อมูล
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal: Deposit History */}
            {showDepositHistoryModal && selectedCustomer && (
                <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    ประวัติเงินมัดจำ: {selectedCustomer.name}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowDepositHistoryModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                {loadingDepositHistory ? (
                                    <SkeletonLoader type="table" rows={4} columns={5} />
                                ) : depositHistory.length === 0 ? (
                                    <div className="text-center py-4 text-muted">ไม่พบข้อมูลมัดจำ</div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th>วันที่</th>
                                                    <th className="text-end">ยอดมัดจำ</th>
                                                    <th className="text-end">คงเหลือ</th>
                                                    <th className="text-center">สถานะ</th>
                                                    <th>Note</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {depositHistory.map((d) => (
                                                    <tr key={d.id}>
                                                        <td>{new Date(d.created_at || d.date).toLocaleString('th-TH')}</td>
                                                        <td className="text-end fw-bold text-success">+{d.amount_initial.toLocaleString()}</td>
                                                        <td className="text-end fw-bold">{d.amount_remaining.toLocaleString()}</td>
                                                        <td className="text-center">
                                                            <span className={`badge ${d.status === 'active' ? 'bg-success' : d.status === 'used' ? 'bg-secondary' : 'bg-danger'}`}>
                                                                {d.status === 'active' ? 'ใช้งานอยู่' : d.status === 'used' ? 'ใช้หมดแล้ว' : 'ยกเลิกคืนเงิน'}
                                                            </span>
                                                        </td>
                                                        <td>{d.note || "-"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowDepositHistoryModal(false)}>
                                    ปิด
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomersPage;
